// Re:Vault - central settings schema. Single source of truth for every tweakable.
// Imported by the app, the settings page, and (for some keys) reflected into the SW.

import { seasonGroup } from "./seasons.js";

export const SETTINGS_KEY = "revault:settings";
// 2: Scramjet 2 became the default engine ("scramjet2"). See migrateSettings() below.
// 4: Clean Solid became the default look. See settleLook() below.
export const SETTINGS_VERSION = 4;

export const DEFAULTS = {
	_v: SETTINGS_VERSION,

	// --- Proxy & engine (protocol is ALWAYS wisp via mrrowisp - not tweakable) ---
	engine: "scramjet2", // scramjet2 (Standard) | scramjet (Standard (v1)) | uv (Compatible)
	transport: "libcurl", // libcurl | epoxy | server
	searchEngine: "duckduckgo", // duckduckgo | google | bing | custom
	customSearch: "https://duckduckgo.com/?q=%s", // used when searchEngine === custom
	suggestions: true, // live search-suggestion dropdown in the address bar (via same-origin /suggest)
	obfuscation: "standard", // off | standard (under-side) | max (also obfuscate rendered URLs/titles)

	// --- Appearance & theme (universal theme system) ---
	theme: "clean", // default look is Clean Solid
	accent: "#f6b93b",
	font: "rounded", // Figtree, with Bricolage Grotesque for headings (a self-hosted web font; see perf note)
	animations: true,
	density: "comfortable", // comfortable | compact
	// Performance / Lite mode for low-end laptops + Chromebooks. "auto" turns the
	// lite treatment on for slower devices (few cores / little RAM / Data Saver);
	// "on" forces it; "off" never applies it. See app/perf.js + [data-perf] in CSS.
	performanceMode: "auto", // auto | on | off
	// Layout (docs/ISLAND-MODE.md): "classic" (tabs + toolbar) or "island" (one island holds it all).
	// Desktop is its own place (/os/), not a value here.
	layout: "classic",
	islandPosition: "top", // top | bottom
	islandEffects: "auto", // auto (follows Lite mode) | full | normal | low
	islandTucked: false, // tucked away to a small grabber for reading or video
	clockShow: true,
	clock24h: false,
	clockSeconds: false, // a ticking seconds digit is busy for a page you look at all day
	showShortcuts: true,
	showBrand: true, // Re:Vault logo + name in the top-left tab strip
	bumpers: "visit", // visit | day | pinned | off: the Vee bumper on the start page (app/bumper-gate.js, docs/BUMPERS.md)
	bumperPin: "", // the pinned bumper while bumpers === "pinned": a core id ("vee-math") or a pack id ("2026-11-wave-1/exam")
	showStatus: true, // the connection status button in the toolbar
	showLoader: true, // the "Loading <site>…" screen that covers a page while it loads
	cornerStyle: "rounded", // sharp | rounded | soft - UI corner radius (--radius)
	greetingName: "", // optional name appended to the start-screen greeting
	homeTouches: true, // greeting variety on the start page; nothing else reads it
	showContinue: true, // "Jump back in" row: books, tools, a deck, open tasks
	homeWeek: true, // the "This week" card on the start page (docs/HOME-2026-10.md, F6)
	homeTip: true, // the daily tip line in that card (F7)
	homeOnline: true, // the rounded "About N students on Re:Vault right now" line in that card
	studyingNow: true, // the start page (and Island) line "About N people are studying right now"
	homeGoal: null, // weekly goal { kind: "days" | "fo" | "fc" | "td", n }, set from the card
	focusSound: "off", // "off" | a MUSIC_PRESETS id, played while a Pomodoro focus runs (set from the timer)
	focusSync: false, // the Pomodoro starts with everyone at :00 and :30 (shared/commons.js)
	commonsGarden: true, // the Commons garden line, and adding finished focus minutes to its weekly total
	streakShow: true, // the school-day streak on the start page and in the Island (shared/streak.js)
	streakSummer: true, // pause the streak over the summer break
	streakSummerFrom: "05-25", // "MM-DD", every year
	streakSummerTo: "08-31",

	// --- Wallpaper & background (see app/wallpaper.js) ---
	// "none" | a WALLPAPER_PRESETS id | "custom" (uploaded image, stored under
	// WALLPAPER_KEY) | "animated" (uploaded GIF/MP4/WebM, see wallpaper-media.js) |
	// "url" (remote image, GIF or video link) | "solid" (flat color).
	wallpaper: "none",
	wallpaperUrl: "", // image URL when wallpaper === "url"
	wallpaperSolid: "#0a0a0a", // flat color when wallpaper === "solid"
	wallpaperFit: "cover", // cover | contain | center | tile
	wallpaperBlur: 0, // 0-40 px gaussian blur
	wallpaperDim: 35, // 0-85 % black scrim over the image (text legibility)
	wallpaperScope: "start", // start (new-tab screen only) | app (behind the whole shell)
	wallpaperAnimate: true, // play animated wallpapers (GIF/MP4/WebM); Lite mode and reduced motion still show a still frame
	snowfall: false, // a minute of light snow on the start page during the winter season (opt-in; off with less motion)

	// --- Cloaking & stealth ---
	cloakPreset: "none", // none | google | classroom | drive | docs | gmail | custom
	cloakTitle: "", // custom title (when cloakPreset === custom)
	cloakFavicon: "", // custom favicon URL (when cloakPreset === custom)
	cloakOnBlur: false, // auto-cloak when the browser tab loses focus
	aboutBlank: false, // open Re:Vault inside an about:blank shell
	panicEnabled: false,
	panicKey: "`", // single key that triggers the panic redirect
	panicUrl: "https://classroom.google.com",
	antiClose: false, // warn before closing/leaving the page

	// --- General & behavior ---
	newTab: "start", // start | blank | url - what a fresh tab shows
	newTabUrl: "", // proxied URL opened when newTab === "url"
	homepage: "", // optional home button target (proxied); blank hides the button
	bookmarksBar: false, // show the saved-bookmarks strip under the toolbar
	keepHistory: true, // record visited sites (also gated by incognito)
	confirmCloseMany: true, // warn before closing a window with several tabs
	maxLiveTabs: 6, // how many tabs keep a live iframe; the rest suspend (reload on reopen)
	defaultZoom: 100, // starting page zoom for new tabs (50-200)
	paletteEnabled: true, // ⌘/Ctrl-K command palette
	slashShortcut: true, // "/" jumps to the address bar (a single-key shortcut, so it can be turned off)
	showRecents: true, // recents row on the start screen
	sessionRestore: true, // offer to reopen the last session's tabs ("Restore N tabs")
	autoUpdate: true, // auto-reload onto a new deploy when it's safe (no active proxied tab) - see app/update.js
	updateNotify: true, // show an "update available" toast when a new deploy is detected
	whatsNew: true, // the "What's new" note on the start page after an update
	installPrompt: true, // the once-a-week "Install Re:Vault" note (app/install.js)
	chromeCollapsed: false, // top chrome (tabs + toolbar) collapsed behind a pull tab
	// which start-screen shortcuts are shown
	scBooks: true,
	scWorksheets: true,
	scPartners: true,
	scTools: true,
	scForums: true,
	scHall: false, // Study Hall (live moderated chat). Off until general availability (docs/chat/DESIGN.md 20)
	scQuests: true,
	scLeaderboard: true,
	scTV: true,
	scMusic: true,
	scBookmarks: true,
	scHistory: true,
	scHelp: true,
	scLinux: true,
	scDesktop: true,
	pinTool: "", // one study tool pinned to the start page (a tools-manifest id), or none

	// --- Extensions (opt-in; logic loads only when enabled - see shared/extensions.js
	// for the registry, app/extensions.js for the SW relay, app/ext-frames.js for
	// page-tool injection). Toggled in Settings → Extensions or the toolbar panel.
	// Former base features (bell/music/palette/suggestions) are ALSO extensions but
	// keep their original keys above. ---
	extAdblock: false, // block ads + trackers on proxied pages (service-worker network block)
	extBandwidth: false, // block autoplay video / heavy media on proxied pages (low-end + slow-network saver)
	extDevtools: false, // inject Eruda devtools into proxied pages (console/elements/network)
	extDarkMode: false, // force a dark look on bright proxied pages (CSS filter)
	extReadAloud: false, // read the page / selection aloud via speech synthesis
	extHighlighter: false, // select text on proxied pages to highlight it (in-session)
	extPip: false, // pop the current page's video into Picture-in-Picture
	extVencord: false, // Vencord (beta) in Discord tabs, Standard only (app/vencord.js); on only after its warning (consent.js)
	extHelper: true, // Study Helper button + side panel (still needs the server flag)
	// Toolbar buttons for extensions (shared/ext-pins.js). Page tools get a button when turned on;
	// extPinned holds the ones pinned by hand, in toolbar order, extUnpinned the ones taken off.
	extPinned: [],
	extUnpinned: [],
	// Read Aloud (app/read-aloud.js). Speech stays on this device unless readOnline is on.
	readVoice: "", // a voice name; blank = the best voice on this device for the page's language
	readRate: 1, // 0.75 - 2
	readMainOnly: true, // read the article, not menus and footers
	readHighlight: true, // mark the sentence and word being read
	readFollow: true, // scroll along with the reading
	readRemember: true, // offer to continue where you stopped (never in incognito or with history off)
	readOnline: false, // allow voices that send the text to a speech service

	// --- Class bell schedule (see app/bell.js). Client-side only. A small in-app
	// reminder fires a few minutes before each bell time you set. ---
	bellEnabled: false,
	bellWarnMin: 5, // minutes before a bell to warn (0 = only at the bell)
	bellChime: true, // play a soft chime with the reminder
	bellNotify: false, // also raise a desktop notification (asks permission when turned on)
	bellDays: [1, 2, 3, 4, 5], // weekdays the schedule is active (0=Sun … 6=Sat)
	bellSchedule: [], // [{ t: "HH:MM", label: "" }] - the bell times, e.g. period ends

	// --- Background music / focus sounds (see app/music.js). Off by default; a
	// compact player appears when on. Presets are self-contained (Web Audio, no
	// files); your own uploads are stored only in this browser (IndexedDB). ---
	musicEnabled: false,
	musicPreset: "brown", // a MUSIC_PRESETS id (soundscape) | "uploads" (your files)
	musicVolume: 45, // 0-100
	musicLoop: true, // loop the current track / soundscape

	// --- Linux (the /linux page, see src/client/linux/). Machines, snapshots and disks live in
	// this browser (shared/linux-store.js); these are only the preferences. ---
	linuxEngine: "v86", // v86 | cheerpx: which kind of machine the page opens (and the Linux tile boots) by default
	linuxCheerpx: true, // offer CheerpX machines (Debian terminal, Alpine desktop); licence notice still blocks first load
	linuxMemory: 256, // MB of RAM for new v86 machines
	linuxNetwork: true, // let v86 guests reach the internet through Re:Vault's Linux relay
	linuxWispUrl: "", // advanced: a different ws(s):// Wisp endpoint for v86 guests; blank = Re:Vault's own

	// --- Privacy & data ---
	incognito: false, // do not persist per-tab history across reloads
	adsEnabled: true, // "Show ads": labeled slots on the start page + library (docs/ADS-PLUMBING.md); off = no ad code loads at all
};

// localStorage keys for the persisted feature stores (see shared/store.js).
export const FAVORITES_KEY = "revault:favorites"; // unified favorites: sites + books + apps
export const BOOKMARKS_KEY = "revault:bookmarks"; // legacy site bookmarks (migrated into favorites)
export const HISTORY_KEY = "revault:history";
export const HISTORY_LIMIT = 300;
export const SESSION_KEY = "revault:session"; // the open tabs, saved as you browse
export const SESSION_OFFER_KEY = "revault:session-offer"; // last session's tabs, waiting for "Restore"
export const AUTO_RESTORE_KEY = "rv:auto-restore"; // sessionStorage: Re:Vault reloaded itself, so reopen without asking
// The uploaded custom wallpaper (a downscaled data: URL). Stored OUTSIDE the
// settings blob - see shared/store.js - so it never bloats the broadcast channel
// or the settings export/import. Cross-tab sync rides the storage event.
export const WALLPAPER_KEY = "revault:wallpaper-image";
// The animated wallpaper's index record (kind, size, poster frame); the file itself is in
// IndexedDB (see shared/wallpaper-media.js).
export const WALLPAPER_MEDIA_KEY = "revault:wallpaper-media";

// First-run welcome + setup (docs/ONBOARDING-2026-10.md). One JSON record under
// ONBOARDING_KEY: { v, status, offers, at, step, picks }, where status is
// "pending" (first visit, chip not shown yet), "offered", "snoozed", "done" or
// "dismissed". The gate is app/onboarding-gate.js; the chip and dialog are a lazy
// bundle (src/client/onboarding/). To show a new version once to people who
// already finished an older one, raise VERSION and RESHOW_BELOW together.
export const ONBOARDING_KEY = "revault:onboarding";
export const ONBOARDING_LEGACY_KEY = "revault:onboarded"; // the v1 spotlight tour: "1" = finished it
export const ONBOARDING_VERSION = 2;
export const ONBOARDING_RESHOW_BELOW = 2;
export const ONBOARDING_MAX_OFFERS = 3;
// The six one-click looks in setup step 1 (BUNDLES ids). The first is the default look.
export const ONBOARDING_LOOKS = ["clean", "clean-paper", "zen", "nightfall", "aurora", "frutiger"];
// The same six as objects (defined beside BUNDLES, in the order above).
export const ONBOARDING_BUNDLES = () => [LOOK_CLEAN, LOOK_CLEAN_PAPER, LOOK_ZEN, LOOK_NIGHTFALL, LOOK_AURORA, LOOK_FRUTIGER];

// Start-screen shortcut registry. `view` opens a folder view; `page` opens an
// internal BiB page. `flag` is the settings key that toggles visibility.
export const SHORTCUTS = [
	{ id: "books", label: "Books", view: "books", flag: "scBooks" },
	{ id: "worksheets", label: "Worksheets", view: "worksheets", flag: "scWorksheets", net: 1 },
	{ id: "partners", label: "Partners", view: "partners", flag: "scPartners", net: 1 },
	{ id: "tools", label: "Tools", view: "tools", flag: "scTools" },
	// A whole Linux computer in a tab (v86 / CheerpX). `boot` = one click starts the default machine.
	{ id: "linux", label: "Linux", page: "/linux", flag: "scLinux", net: 1, boot: true },
	// Re:Vault OS: the same Re:Vault as a desktop with windows (/os/). `env` switches the whole page.
	{ id: "desktop", label: "Desktop", env: "/os/", flag: "scDesktop" },
	{ id: "forums", label: "Forums", view: "forums", flag: "scForums", net: 1 },
	// Study Hall: live study rooms with moderators (an internal page, /hall). `preview` keeps it out
	// of All shortcuts until it is open to everyone; Settings still has its switch.
	{ id: "hall", label: "Study Hall", page: "/hall", flag: "scHall", net: 1, preview: true },
	{ id: "quests", label: "Quests", view: "quests", flag: "scQuests" },
	{ id: "leaderboard", label: "Leaderboard", view: "leaderboard", flag: "scLeaderboard", net: 1 },
	// `url` shortcuts open a proxied site in a new tab (vs. `view`=folder / `page`=BiB page).
	// `net`: needs the connection, so the start page leaves it out while offline.
	{ id: "tv", label: "TV", url: "https://movy.sx/", flag: "scTV", net: 1 },
	{ id: "music", label: "Music", url: "https://soundcloud.com/discover", flag: "scMusic", net: 1 },
	{ id: "favorites", label: "Favorites", view: "favorites", flag: "scBookmarks" },
	{ id: "history", label: "History", view: "history", flag: "scHistory" },
	{ id: "help", label: "Help", page: "/help", flag: "scHelp" },
];

// Accent swatches offered in the theme picker.
export const ACCENT_PRESETS = ["#f6b93b", "#e9c46a", "#ff8a3d", "#ff5a5a", "#ff5d8f", "#b388ff", "#818cf8", "#4f9cff", "#38bdf8", "#2dd4bf", "#27c093", "#a3e635", "#e6e6e6"];

// Base color schemes. `mode` (light/dark) drives the light-specific CSS (veil,
// wallpaper halo) and the "auto" resolution; the palette tokens themselves live in
// theme.css as :root[data-theme="<id>"] blocks. Accent stays independent.
export const THEMES = {
	// Standard
	dark: { label: "Dark", mode: "dark", group: "Standard" },
	slate: { label: "Slate", mode: "dark", group: "Standard" },
	midnight: { label: "Midnight", mode: "dark", group: "Standard" },
	plum: { label: "Plum", mode: "dark", group: "Standard" },
	forest: { label: "Forest", mode: "dark", group: "Standard" },
	crimson: { label: "Crimson", mode: "dark", group: "Standard" },
	ocean: { label: "Ocean", mode: "dark", group: "Standard" },
	rose: { label: "Rosé", mode: "dark", group: "Standard" },
	mocha: { label: "Mocha", mode: "dark", group: "Standard" },
	nord: { label: "Nord", mode: "dark", group: "Standard" },
	dracula: { label: "Dracula", mode: "dark", group: "Standard" },
	// Vocaloid
	miku: { label: "Miku", mode: "dark", group: "Vocaloid" },
	teto: { label: "Teto", mode: "dark", group: "Vocaloid" },
	rin: { label: "Rin", mode: "dark", group: "Vocaloid" },
	gumi: { label: "GUMI", mode: "dark", group: "Vocaloid" },
	// Naruto
	sasuke: { label: "Sasuke", mode: "dark", group: "Naruto" },
	sakura: { label: "Sakura", mode: "dark", group: "Naruto" },
	kakashi: { label: "Kakashi", mode: "dark", group: "Naruto" },
	itachi: { label: "Itachi", mode: "dark", group: "Naruto" },
	hinata: { label: "Hinata", mode: "dark", group: "Naruto" },
	// SML
	jeffy: { label: "Jeffy", mode: "dark", group: "SML" },
	// Nature
	woodland: { label: "Woodland", mode: "dark", group: "Nature" },
	tide: { label: "Tide", mode: "dark", group: "Nature" },
	alpine: { label: "Alpine", mode: "dark", group: "Nature" },
	autumn: { label: "Autumn", mode: "dark", group: "Nature" },
	bloom: { label: "Bloom", mode: "dark", group: "Nature" },
	// Cars
	jdm: { label: "JDM", mode: "dark", group: "Cars" },
	lambo: { label: "Lamborghini", mode: "dark", group: "Cars" },
	gtr: { label: "GT-R", mode: "dark", group: "Cars" },
	ferrari: { label: "Ferrari", mode: "dark", group: "Cars" },
	// School
	chalkboard: { label: "Chalkboard", mode: "dark", group: "School" },
	notebook: { label: "Notebook", mode: "light", group: "School" },
	library: { label: "Library", mode: "dark", group: "School" },
	// Featured. Clean Solid is Vee's own ink, paper and gold: no gradients, no blur, no glow.
	"clean": { label: "Clean Solid", mode: "dark", group: "Featured" },
	"clean-paper": { label: "Clean Paper", mode: "light", group: "Featured" },
	"bedazzled": { label: "Bedazzled Vault", mode: "dark", group: "Featured" },
	"keystone-cat": { label: "Keystone Cat", mode: "dark", group: "Featured" },
	// Calm: ink and rice paper, one vermilion seal
	"zen": { label: "Zen", mode: "dark", group: "Calm" },
	"zen-paper": { label: "Zen Paper", mode: "light", group: "Calm" },
	// Scenery
	"vista": { label: "Vista", mode: "dark", group: "Scenery" },
	"canyon": { label: "Canyon", mode: "dark", group: "Scenery" },
	"coast": { label: "Coast", mode: "dark", group: "Scenery" },
	// City
	"skyline": { label: "Skyline", mode: "dark", group: "City" },
	"neon": { label: "Neon", mode: "dark", group: "City" },
	// Aesthetic
	"synthwave": { label: "Synthwave", mode: "dark", group: "Aesthetic" },
	"vaporwave": { label: "Vaporwave", mode: "dark", group: "Aesthetic" },
	"lofi": { label: "Lo-Fi", mode: "dark", group: "Aesthetic" },
	"frutiger": { label: "Frutiger Aero", mode: "light", group: "Aesthetic" },
	"frutiger-dark": { label: "Frutiger Aero Dark", mode: "dark", group: "Aesthetic" },
	// Seasonal: `season` is a shared/seasons.js id. Pickers offer it only during that week; anyone
	// already using it keeps it afterwards.
	spooky: { label: "Spooky", mode: "dark", group: "Seasonal", season: "spooky-2026" },
	// Accessibility: 7:1 text, solid surfaces and borders, strong focus rings
	contrast: { label: "High Contrast", mode: "dark", group: "Accessibility" },
	// Light
	light: { label: "Light", mode: "light", group: "Light" },
	sepia: { label: "Sepia", mode: "light", group: "Light" },
	auto: { label: "Match system", mode: "dark" },
};
// Theme ids that are light-mode (kept in sync for the pre-paint script in index.html).
export const LIGHT_THEMES = ["light", "sepia", "notebook", "frutiger", "zen-paper", "clean-paper"];
// Render order for the grouped theme picker.
export const THEME_GROUP_ORDER = ["Featured", "Seasonal", "Accessibility", "Calm", "Standard", "Vocaloid", "Naruto", "SML", "Nature", "Cars", "School", "Scenery", "City", "Aesthetic", "Light"];

// Built-in wallpaper presets shown in the picker.
//   kind "image"    -> a self-hosted file under /wallpapers/ (served first-party;
//                      the SW caches it cache-first on first use, not precached).
//   kind "gradient" -> a pure-CSS background-image value (zero bytes).
// `nightfall` is the community-requested artwork; the rest are generated meshes.
// Order here is the order they render in the gallery.
export const WALLPAPER_PRESETS = [
	{ id: "nightfall", label: "Nightfall", kind: "image", value: "/wallpapers/nightfall.webp" },
	{ id: "aurora", label: "Aurora", kind: "image", value: "/wallpapers/aurora.webp" },
	{ id: "nebula", label: "Nebula", kind: "image", value: "/wallpapers/nebula.webp" },
	{ id: "sunset", label: "Sunset", kind: "image", value: "/wallpapers/sunset.webp" },
	{ id: "abyss", label: "Abyss", kind: "image", value: "/wallpapers/abyss.webp" },
	{ id: "miku", label: "Miku", kind: "image", value: "/wallpapers/miku.webp" },
	{ id: "teto", label: "Teto", kind: "image", value: "/wallpapers/teto.webp" },
	{ id: "rin", label: "Rin", kind: "image", value: "/wallpapers/rin.webp" },
	{ id: "gumi", label: "GUMI", kind: "image", value: "/wallpapers/gumi.webp" },
	{ id: "sasuke", label: "Sasuke", kind: "image", value: "/wallpapers/sasuke.webp" },
	{ id: "sakura", label: "Sakura", kind: "image", value: "/wallpapers/sakura.webp" },
	{ id: "kakashi", label: "Kakashi", kind: "image", value: "/wallpapers/kakashi.webp" },
	{ id: "itachi", label: "Itachi", kind: "image", value: "/wallpapers/itachi.webp" },
	{ id: "hinata", label: "Hinata", kind: "image", value: "/wallpapers/hinata.webp" },
	{ id: "jeffy", label: "Jeffy", kind: "image", value: "/wallpapers/jeffy.webp" },
	{ id: "woodland", label: "Woodland", kind: "image", value: "/wallpapers/woodland.webp" },
	{ id: "tide", label: "Tide", kind: "image", value: "/wallpapers/tide.webp" },
	{ id: "alpine", label: "Alpine", kind: "image", value: "/wallpapers/alpine.webp" },
	{ id: "autumn", label: "Autumn", kind: "image", value: "/wallpapers/autumn.webp" },
	{ id: "bloom", label: "Bloom", kind: "image", value: "/wallpapers/bloom.webp" },
	{ id: "jdm", label: "JDM", kind: "image", value: "/wallpapers/jdm.webp" },
	{ id: "lambo", label: "Lamborghini", kind: "image", value: "/wallpapers/lambo.webp" },
	{ id: "gtr", label: "GT-R", kind: "image", value: "/wallpapers/gtr.webp" },
	{ id: "ferrari", label: "Ferrari", kind: "image", value: "/wallpapers/ferrari.webp" },
	{ id: "chalkboard", label: "Chalkboard", kind: "image", value: "/wallpapers/chalkboard.webp" },
	{ id: "notebook", label: "Notebook", kind: "image", value: "/wallpapers/notebook.webp" },
	{ id: "library", label: "Library", kind: "image", value: "/wallpapers/library.webp" },
	{ id: "keystone-cat", label: "Keystone Cat", kind: "image", value: "/wallpapers/keystone-cat.webp" },
	{ id: "vista", label: "Vista", kind: "image", value: "/wallpapers/vista.webp" },
	{ id: "canyon", label: "Canyon", kind: "image", value: "/wallpapers/canyon.webp" },
	{ id: "coast", label: "Coast", kind: "image", value: "/wallpapers/coast.webp" },
	{ id: "skyline", label: "Skyline", kind: "image", value: "/wallpapers/skyline.webp" },
	{ id: "neon", label: "Neon", kind: "image", value: "/wallpapers/neon.webp" },
	{ id: "synthwave", label: "Synthwave", kind: "image", value: "/wallpapers/synthwave.webp" },
	{ id: "vaporwave", label: "Vaporwave", kind: "image", value: "/wallpapers/vaporwave.webp" },
	{ id: "lofi", label: "Lo-Fi", kind: "image", value: "/wallpapers/lofi.webp" },
	{ id: "dog", label: "Pup", kind: "image", value: "/wallpapers/dog.webp" },
	// A small hand-written SVG, so it needs no thumbnails. Seasonal like the Spooky theme.
	{ id: "spooky", label: "Harvest Moon", kind: "image", value: "/wallpapers/spooky.svg", season: "spooky-2026" },
	// The Holiday Update (winter-2026): a string of lights over a small tree, and quiet snowy pines.
	{ id: "winter-lights", label: "Holiday Lights", kind: "image", value: "/wallpapers/winter-lights.svg", season: "winter-2026" },
	{ id: "winter-pines", label: "Snowy Pines", kind: "image", value: "/wallpapers/winter-pines.svg", season: "winter-2026" },
	{ id: "dusk", label: "Dusk", kind: "gradient", value: "linear-gradient(160deg,#1a2a6c 0%,#0f0c29 70%)" },
	{ id: "ember", label: "Ember", kind: "gradient", value: "linear-gradient(160deg,#42112b 0%,#0a0a0a 72%)" },
	{ id: "deepsea", label: "Deep Sea", kind: "gradient", value: "radial-gradient(120% 120% at 50% 0%,#0b3b54 0%,#05070d 70%)" },
	{ id: "slate", label: "Mono Slate", kind: "gradient", value: "linear-gradient(180deg,#23262b 0%,#0c0d10 100%)" },
	{ id: "blaze", label: "Blaze", kind: "gradient", value: "linear-gradient(160deg,#7a3a0c 0%,#1a0e05 72%)" },
	{ id: "voltage", label: "Voltage", kind: "gradient", value: "linear-gradient(160deg,#6e7a0c 0%,#12140a 72%)" },
	{ id: "petal", label: "Petal", kind: "gradient", value: "linear-gradient(160deg,#7a2a48 0%,#1a0e14 72%)" },
	{ id: "shadow", label: "Shadow", kind: "gradient", value: "radial-gradient(120% 120% at 50% 0%,#5a0c14 0%,#0a0506 70%)" },
	{ id: "evergreen", label: "Evergreen", kind: "gradient", value: "linear-gradient(160deg,#1a5a30 0%,#08120c 72%)" },
	{ id: "lagoon", label: "Lagoon", kind: "gradient", value: "radial-gradient(120% 120% at 50% 0%,#0b5a6e 0%,#05121a 70%)" },
	{ id: "lavender", label: "Lavender", kind: "gradient", value: "linear-gradient(160deg,#4a2f8a 0%,#120c1e 72%)" },
	{ id: "sand", label: "Sand", kind: "gradient", value: "linear-gradient(160deg,#7a5a2a 0%,#1a1208 72%)" },
	{ id: "frost", label: "Frost", kind: "gradient", value: "linear-gradient(160deg,#2a4a6e 0%,#0c1218 72%)" },
	{ id: "racing", label: "Racing", kind: "gradient", value: "linear-gradient(160deg,#7a1018 0%,#160708 72%)" },
];
export const WALLPAPER_PRESET_MAP = /* @__PURE__ */ Object.fromEntries(/* @__PURE__ */ WALLPAPER_PRESETS.map((w) => [w.id, w]));

// The settings keys an appearance "bundle" captures and applies as a coordinated
// look. Applying a bundle only touches these keys (composes with everything else).
export const BUNDLE_KEYS = [
	"theme", "accent", "font", "density", "cornerStyle",
	"wallpaper", "wallpaperScope", "wallpaperFit", "wallpaperDim", "wallpaperBlur", "wallpaperSolid", "wallpaperUrl",
];

// Built-in one-click looks. Each pairs a theme + accent + font + density + corners
// with a wallpaper (and its scope/dim/fit) so the whole app changes character at
// once. Users can still fine-tune any control afterward, and save their own.
// The six looks first-run setup offers (ONBOARDING_LOOKS) are named here and placed in BUNDLES
// below, so the lazy setup bundle can import just these six instead of the whole list.
const LOOK_CLEAN = { id: "clean", label: "Clean Solid", settings: { theme: "clean", accent: "#f6b93b", font: "rounded", density: "comfortable", cornerStyle: "rounded", wallpaper: "none" } };
const LOOK_CLEAN_PAPER = { id: "clean-paper", label: "Clean Paper", settings: { theme: "clean-paper", accent: "#f6b93b", font: "rounded", density: "comfortable", cornerStyle: "rounded", wallpaper: "none" } };
const LOOK_BEDAZZLED_VAULT = { id: "bedazzled-vault", label: "Bedazzled Vault", settings: { theme: "bedazzled", accent: "#e9c46a", font: "sora", density: "comfortable", cornerStyle: "soft", wallpaper: "none" } };
const LOOK_NIGHTFALL = { id: "nightfall", label: "Nightfall", settings: { theme: "dark", accent: "#39d4c4", font: "rounded", density: "comfortable", cornerStyle: "soft", wallpaper: "nightfall", wallpaperScope: "start", wallpaperFit: "cover", wallpaperDim: 45, wallpaperBlur: 0 } };
const LOOK_AURORA = { id: "aurora", label: "Aurora", settings: { theme: "forest", accent: "#27c093", font: "sora", density: "comfortable", cornerStyle: "soft", wallpaper: "aurora", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 28, wallpaperBlur: 0 } };
const LOOK_PAPER = { id: "paper", label: "Paper", settings: { theme: "sepia", accent: "#4f9cff", font: "cormorant", density: "comfortable", cornerStyle: "rounded", wallpaper: "none" } };
const LOOK_FRUTIGER = { id: "frutiger", label: "Frutiger Aero", settings: { theme: "frutiger", accent: "#2dd4bf", font: "quicksand", density: "comfortable", cornerStyle: "soft", wallpaper: "none" } };
// Zen: sumi ink and washi paper with a single vermilion seal. Its texture, ensō and seal
// live in theme.css, so no wallpaper.
const LOOK_ZEN = { id: "zen", label: "Zen", settings: { theme: "zen", accent: "#dd6b4d", font: "zen-gothic", density: "comfortable", cornerStyle: "soft", wallpaper: "none" } };
const LOOK_ZEN_PAPER = { id: "zen-paper", label: "Zen Paper", settings: { theme: "zen-paper", accent: "#b0392b", font: "zen-gothic", density: "comfortable", cornerStyle: "soft", wallpaper: "none" } };
// High Contrast: black, white and one yellow, Atkinson Hyperlegible (made for low vision), no
// wallpaper. The theme keeps its own yellow whatever accent is picked (theme.css).
const LOOK_HIGH_CONTRAST = { id: "high-contrast", label: "High Contrast", settings: { theme: "contrast", accent: "#ffd60a", font: "atkinson", density: "comfortable", cornerStyle: "rounded", wallpaper: "none" } };
const LOOK_MONO = { id: "mono", label: "Mono", settings: { theme: "dark", accent: "#e6e6e6", font: "jetbrains", density: "compact", cornerStyle: "sharp", wallpaper: "none" } };

export const BUNDLES = [
	LOOK_CLEAN,
	LOOK_CLEAN_PAPER,
	{ id: "vault", label: "Vault", settings: { theme: "dark", accent: "#e9c46a", font: "verdana", density: "comfortable", cornerStyle: "rounded", wallpaper: "none" } },
	// Bedazzled Vault: a glossy black-and-gold modern take on the default Vault.
	// Self-contained warm gradient + gold gel (see theme.css), so no wallpaper needed.
	LOOK_BEDAZZLED_VAULT,
	LOOK_ZEN,
	LOOK_ZEN_PAPER,
	LOOK_HIGH_CONTRAST,
	LOOK_NIGHTFALL,
	LOOK_AURORA,
	{ id: "nebula", label: "Nebula", settings: { theme: "plum", accent: "#b388ff", font: "comfortaa", density: "comfortable", cornerStyle: "soft", wallpaper: "nebula", wallpaperScope: "start", wallpaperFit: "cover", wallpaperDim: 35, wallpaperBlur: 0 } },
	{ id: "sunset", label: "Sunset", settings: { theme: "dark", accent: "#ff8a3d", font: "baloo", density: "comfortable", cornerStyle: "soft", wallpaper: "sunset", wallpaperScope: "start", wallpaperFit: "cover", wallpaperDim: 35, wallpaperBlur: 0 } },
	{ id: "deepsea", label: "Deep Sea", settings: { theme: "midnight", accent: "#4f9cff", font: "manrope", density: "comfortable", cornerStyle: "rounded", wallpaper: "deepsea", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 22, wallpaperBlur: 0 } },
	LOOK_PAPER,
	LOOK_MONO,
	{ id: "voc-miku", label: "Miku", settings: { theme: "miku", accent: "#39c5bb", font: "baloo", density: "comfortable", cornerStyle: "soft", wallpaper: "miku", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 40, wallpaperBlur: 2 } },
	{ id: "voc-teto", label: "Teto", settings: { theme: "teto", accent: "#e24a6e", font: "rajdhani", density: "comfortable", cornerStyle: "rounded", wallpaper: "teto", wallpaperScope: "start", wallpaperFit: "cover", wallpaperDim: 45, wallpaperBlur: 0 } },
	{ id: "voc-rin", label: "Rin", settings: { theme: "rin", accent: "#ffaa00", font: "fredoka", density: "comfortable", cornerStyle: "soft", wallpaper: "rin", wallpaperScope: "start", wallpaperFit: "cover", wallpaperDim: 38, wallpaperBlur: 0 } },
	{ id: "voc-gumi", label: "GUMI", settings: { theme: "gumi", accent: "#44cf72", font: "sora", density: "comfortable", cornerStyle: "soft", wallpaper: "gumi", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 35, wallpaperBlur: 0 } },
	{ id: "c-sasuke", label: "Sasuke", settings: { theme: "sasuke", accent: "#7d82e0", font: "oswald", density: "compact", cornerStyle: "sharp", wallpaper: "sasuke", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 32, wallpaperBlur: 1 } },
	{ id: "c-sakura", label: "Sakura", settings: { theme: "sakura", accent: "#ff8bb0", font: "comfortaa", density: "comfortable", cornerStyle: "soft", wallpaper: "sakura", wallpaperScope: "start", wallpaperFit: "cover", wallpaperDim: 35, wallpaperBlur: 0 } },
	{ id: "c-kakashi", label: "Kakashi", settings: { theme: "kakashi", accent: "#9fbccc", font: "manrope", density: "comfortable", cornerStyle: "rounded", wallpaper: "kakashi", wallpaperScope: "start", wallpaperFit: "cover", wallpaperDim: 40, wallpaperBlur: 0 } },
	{ id: "c-itachi", label: "Itachi", settings: { theme: "itachi", accent: "#ee4a58", font: "cormorant", density: "compact", cornerStyle: "sharp", wallpaper: "itachi", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 50, wallpaperBlur: 2 } },
	{ id: "c-hinata", label: "Hinata", settings: { theme: "hinata", accent: "#bcaef0", font: "quicksand", density: "comfortable", cornerStyle: "soft", wallpaper: "hinata", wallpaperScope: "start", wallpaperFit: "cover", wallpaperDim: 38, wallpaperBlur: 0 } },
	{ id: "c-jeffy", label: "Jeffy", settings: { theme: "jeffy", accent: "#cfe035", font: "chewy", density: "comfortable", cornerStyle: "soft", wallpaper: "jeffy", wallpaperScope: "start", wallpaperFit: "cover", wallpaperDim: 32, wallpaperBlur: 0 } },
	{ id: "c-woodland", label: "Woodland", settings: { theme: "woodland", accent: "#66b070", font: "cormorant", density: "comfortable", cornerStyle: "soft", wallpaper: "woodland", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 30, wallpaperBlur: 0 } },
	{ id: "c-tide", label: "Tide", settings: { theme: "tide", accent: "#34b8d0", font: "manrope", density: "comfortable", cornerStyle: "rounded", wallpaper: "tide", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 28, wallpaperBlur: 0 } },
	{ id: "c-alpine", label: "Alpine", settings: { theme: "alpine", accent: "#8ab0dc", font: "sora", density: "comfortable", cornerStyle: "rounded", wallpaper: "alpine", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 25, wallpaperBlur: 1 } },
	{ id: "c-autumn", label: "Autumn", settings: { theme: "autumn", accent: "#e8842e", font: "lora", density: "comfortable", cornerStyle: "soft", wallpaper: "autumn", wallpaperScope: "start", wallpaperFit: "cover", wallpaperDim: 35, wallpaperBlur: 0 } },
	{ id: "c-bloom", label: "Bloom", settings: { theme: "bloom", accent: "#ff9bb8", font: "cormorant", density: "comfortable", cornerStyle: "soft", wallpaper: "bloom", wallpaperScope: "start", wallpaperFit: "cover", wallpaperDim: 32, wallpaperBlur: 0 } },
	{ id: "c-jdm", label: "JDM", settings: { theme: "jdm", accent: "#ef3a44", font: "rajdhani", density: "compact", cornerStyle: "sharp", wallpaper: "jdm", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 30, wallpaperBlur: 0 } },
	{ id: "c-lambo", label: "Lamborghini", settings: { theme: "lambo", accent: "#caea2e", font: "orbitron", density: "compact", cornerStyle: "sharp", wallpaper: "lambo", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 30, wallpaperBlur: 0 } },
	{ id: "c-gtr", label: "GT-R", settings: { theme: "gtr", accent: "#5ba0dc", font: "rajdhani", density: "compact", cornerStyle: "sharp", wallpaper: "gtr", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 28, wallpaperBlur: 0 } },
	{ id: "c-ferrari", label: "Ferrari", settings: { theme: "ferrari", accent: "#f0303f", font: "oswald", density: "compact", cornerStyle: "sharp", wallpaper: "ferrari", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 30, wallpaperBlur: 0 } },
	{ id: "c-chalkboard", label: "Chalkboard", settings: { theme: "chalkboard", accent: "#ece2c2", font: "caveat", density: "comfortable", cornerStyle: "rounded", wallpaper: "chalkboard", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 20, wallpaperBlur: 0 } },
	{ id: "c-notebook", label: "Notebook", settings: { theme: "notebook", accent: "#3474d0", font: "caveat", density: "comfortable", cornerStyle: "rounded", wallpaper: "notebook", wallpaperScope: "start", wallpaperFit: "cover", wallpaperDim: 0, wallpaperBlur: 0 } },
	{ id: "c-library", label: "Library", settings: { theme: "library", accent: "#cf9f4c", font: "cormorant", density: "comfortable", cornerStyle: "soft", wallpaper: "library", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 30, wallpaperBlur: 0 } },
	{ id: "keystone-cat", label: "Keystone Cat", settings: { theme: "keystone-cat", accent: "#ef8fad", font: "baloo", density: "comfortable", cornerStyle: "soft", wallpaper: "keystone-cat", wallpaperScope: "start", wallpaperFit: "cover", wallpaperDim: 48, wallpaperBlur: 0 } },
	{ id: "pup", label: "Pup", settings: { theme: "coast", accent: "#5fb0e6", font: "quicksand", density: "comfortable", cornerStyle: "soft", wallpaper: "dog", wallpaperScope: "start", wallpaperFit: "cover", wallpaperDim: 30, wallpaperBlur: 0 } },
	{ id: "c-vista", label: "Vista", settings: { theme: "vista", accent: "#5fb88a", font: "cormorant", density: "comfortable", cornerStyle: "soft", wallpaper: "vista", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 28, wallpaperBlur: 0 } },
	{ id: "c-canyon", label: "Canyon", settings: { theme: "canyon", accent: "#e0843a", font: "oswald", density: "comfortable", cornerStyle: "rounded", wallpaper: "canyon", wallpaperScope: "start", wallpaperFit: "cover", wallpaperDim: 30, wallpaperBlur: 0 } },
	{ id: "c-coast", label: "Coast", settings: { theme: "coast", accent: "#4fb0d8", font: "manrope", density: "comfortable", cornerStyle: "rounded", wallpaper: "coast", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 25, wallpaperBlur: 0 } },
	{ id: "c-skyline", label: "Skyline", settings: { theme: "skyline", accent: "#ffd86a", font: "sora", density: "compact", cornerStyle: "sharp", wallpaper: "skyline", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 25, wallpaperBlur: 0 } },
	{ id: "c-neon", label: "Neon", settings: { theme: "neon", accent: "#ff3da6", font: "orbitron", density: "compact", cornerStyle: "sharp", wallpaper: "neon", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 22, wallpaperBlur: 0 } },
	{ id: "c-synthwave", label: "Synthwave", settings: { theme: "synthwave", accent: "#ff5aa8", font: "russo", density: "comfortable", cornerStyle: "sharp", wallpaper: "synthwave", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 25, wallpaperBlur: 0 } },
	{ id: "c-vaporwave", label: "Vaporwave", settings: { theme: "vaporwave", accent: "#5ad0e0", font: "rajdhani", density: "comfortable", cornerStyle: "rounded", wallpaper: "vaporwave", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 25, wallpaperBlur: 0 } },
	{ id: "c-lofi", label: "Lo-Fi", settings: { theme: "lofi", accent: "#c08a7a", font: "lora", density: "comfortable", cornerStyle: "soft", wallpaper: "lofi", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 30, wallpaperBlur: 0 } },
	// Frutiger Aero - glossy aqua-glass light look. Carries its own sky gradient
	// (see theme.css), so it needs no wallpaper to read as Frutiger Aero.
	LOOK_FRUTIGER,
	// Frutiger Aero Dark - deep aqua "Aero glass". Self-contained dark gradient (see
	// theme.css), so no wallpaper needed.
	{ id: "frutiger-dark", label: "Frutiger Aero Dark", settings: { theme: "frutiger-dark", accent: "#22d3ee", font: "quicksand", density: "comfortable", cornerStyle: "soft", wallpaper: "none" } },
	// The Holiday Update (winter-2026): cranberry on warm charcoal for Christmas, and a calm
	// winter-blue paper look next to it for anyone who'd rather just have snow. Both sit on
	// existing themes, so pre-paint and LIGHT_THEMES need nothing new.
	{ id: "holiday-lights", label: "Holiday Lights", season: "winter-2026", settings: { theme: "lofi", accent: "#b23a48", font: "lora", density: "comfortable", cornerStyle: "soft", wallpaper: "winter-lights", wallpaperScope: "start", wallpaperFit: "cover", wallpaperDim: 20, wallpaperBlur: 0 } },
	{ id: "first-snow", label: "First Snow", season: "winter-2026", settings: { theme: "notebook", accent: "#3f6b9e", font: "nunito", density: "comfortable", cornerStyle: "soft", wallpaper: "winter-pines", wallpaperScope: "start", wallpaperFit: "cover", wallpaperDim: 0, wallpaperBlur: 0 } },
	// Spooky Week (seasonal, see shared/seasons.js): pumpkin on a dusky plum night, with the
	// Harvest Moon wallpaper behind the whole app.
	{ id: "spooky", label: "Spooky Week", season: "spooky-2026", settings: { theme: "spooky", accent: "#f08a24", font: "fredoka", density: "comfortable", cornerStyle: "soft", wallpaper: "spooky", wallpaperScope: "app", wallpaperFit: "cover", wallpaperDim: 35, wallpaperBlur: 0 } },
	// Supporter looks (docs/SUPPORTERS.md 8): anyone can try one on for a minute; keeping it is a
	// Supporter perk. Built from tokens that already exist, so pre-paint needs nothing new. A look
	// may also carry release: "YYYY-MM-DD" (supporters see it 30 days early).
	{ id: "s-gilded-ink", label: "Gilded Ink", signature: true, settings: { theme: "midnight", accent: "#e9c46a", font: "cormorant", density: "comfortable", cornerStyle: "soft", wallpaper: "none" } },
];

// How the Settings Looks grid is sectioned. A look not listed here lands in "More".
export const LOOK_GROUPS = [
	["Featured", ["clean", "clean-paper", "bedazzled-vault", "zen", "nightfall", "aurora", "frutiger", "paper"]],
	// Seasonal sections take their title from shared/seasons.js, newest first.
	[/* @__PURE__ */ seasonGroup("winter-2026"), ["holiday-lights", "first-snow"]],
	[/* @__PURE__ */ seasonGroup("spooky-2026"), ["spooky"]],
	["Easy to read", ["high-contrast"]],
	["Calm", ["zen-paper", "vault", "mono", "c-library", "c-lofi", "c-woodland", "deepsea", "frutiger-dark"]],
	["Characters", ["voc-miku", "voc-teto", "voc-rin", "voc-gumi", "c-sasuke", "c-sakura", "c-kakashi", "c-itachi", "c-hinata", "c-jeffy", "keystone-cat", "pup"]],
	["Places", ["nebula", "sunset", "c-tide", "c-alpine", "c-autumn", "c-bloom", "c-vista", "c-canyon", "c-coast", "c-skyline", "c-neon", "c-synthwave", "c-vaporwave", "c-chalkboard", "c-notebook"]],
	["Cars", ["c-jdm", "c-lambo", "c-gtr", "c-ferrari"]],
	["Supporter looks", ["s-gilded-ink"]],
];

export const BUNDLES_KEY = "revault:bundles"; // user-saved custom bundles (shared/store.js)

// Tab-cloak presets { label, title, favicon }. Favicons self-hosted in /cloaks/
// (sourced from the Interstellar project). custom/none handled specially in cloak.js.
export const CLOAK_PRESETS = {
	none: { label: "Off", title: "", favicon: "" },
	google: { label: "Google", title: "Google", favicon: "/cloaks/google.png" },
	"savvas-realize": { label: "Savvas Realize", title: "Savvas Realize", favicon: "/cloaks/savvas-realize.png" },
	smartpass: { label: "SmartPass", title: "SmartPass", favicon: "/cloaks/smartpass.png" },
	"world-book-online-super-home": { label: "World Book Online - Super Home", title: "Super Home Page", favicon: "/cloaks/wbo.ico" },
	"world-book-online-student": { label: "World Book Online - Student", title: "WBO Student | Home Page", favicon: "/cloaks/wbo.ico" },
	"world-book-online-timelines": { label: "World Book Online - Timelines", title: "Timelines - Home Page", favicon: "/cloaks/wbo.ico" },
	naviance: { label: "Naviance", title: "Naviance Student", favicon: "/cloaks/naviance.png" },
	"pbs-learning-media": { label: "PBS Learning Media", title: "PBS LearningMedia | Teaching Resources For Students And Teachers", favicon: "/cloaks/pbslearningmedia.ico" },
	"pbs-learning-media-student-home": { label: "PBS Learning Media Student Home", title: "Student Homepage | PBS LearningMedia", favicon: "/cloaks/pbslearningmedia.ico" },
	drive: { label: "Drive", title: "My Drive - Google Drive", favicon: "/cloaks/drive.png" },
	classroom: { label: "Classroom", title: "Home", favicon: "/cloaks/classroom.png" },
	schoology: { label: "Schoology", title: "Home | Schoology", favicon: "/cloaks/schoology.png" },
	gmail: { label: "Gmail", title: "Gmail", favicon: "/cloaks/gmail.png" },
	clever: { label: "Clever", title: "Clever | Portal", favicon: "/cloaks/clever.png" },
	khan: { label: "Khan", title: "Dashboard | Khan Academy", favicon: "/cloaks/khan.png" },
	dictionary: { label: "Dictionary", title: "Dictionary.com | Meanings & Definitions of English Words", favicon: "/cloaks/dictionary.png" },
	thesaurus: { label: "Thesaurus", title: "Synonyms and Antonyms of Words | Thesaurus.com", favicon: "/cloaks/thesaurus.png" },
	campus: { label: "Campus", title: "Infinite Campus", favicon: "/cloaks/campus.png" },
	ixl: { label: "IXL", title: "IXL | Dashboard", favicon: "/cloaks/ixl.png" },
	canvas: { label: "Canvas", title: "Dashboard", favicon: "/cloaks/canvas.png" },
	codehs: { label: "CodeHS", title: "Sandbox | CodeHS", favicon: "/cloaks/codehs.png" },
	linkit: { label: "LinkIt", title: "Test Taker", favicon: "/cloaks/linkit.ico" },
	edpuzzle: { label: "Edpuzzle", title: "Edpuzzle", favicon: "/cloaks/edpuzzle.png" },
	"i-ready-math": { label: "i-Ready Math", title: "Math To Do, i-Ready", favicon: "/cloaks/i-ready.ico" },
	"i-ready-reading": { label: "i-Ready Reading", title: "Reading To Do, i-Ready", favicon: "/cloaks/i-ready.ico" },
	"classlink-login": { label: "ClassLink Login", title: "Login", favicon: "/cloaks/classlink-login.png" },
	"google-meet": { label: "Google Meet", title: "Google Meet", favicon: "/cloaks/google-meet.png" },
	"google-docs": { label: "Google Docs", title: "Google Docs", favicon: "/cloaks/google-docs.ico" },
	"google-slides": { label: "Google Slides", title: "Google Slides", favicon: "/cloaks/google-slides.ico" },
	wikipedia: { label: "Wikipedia", title: "Wikipedia", favicon: "/cloaks/wikipedia.png" },
	britannica: { label: "Britannica", title: "Encyclopedia Britannica | Britannica", favicon: "/cloaks/britannica.png" },
	ducksters: { label: "Ducksters", title: "Ducksters", favicon: "/cloaks/ducksters.png" },
	minga: { label: "Minga", title: "Minga - Creating Amazing Schools", favicon: "/cloaks/minga.png" },
	"i-ready-learning-games": { label: "i-Ready Learning Games", title: "Learning Games, i-Ready", favicon: "/cloaks/i-ready.ico" },
	"noredink-home": { label: "NoRedInk Home", title: "Student Home | NoRedInk", favicon: "/cloaks/noredink.webp" },
	desmos: { label: "Desmos", title: "Desmos | Graphing Calculator", favicon: "/cloaks/desmos.ico" },
	"newsela-binder": { label: "Newsela Binder", title: "Newsela | Binder", favicon: "/cloaks/newsela.png" },
	"newsela-assignments": { label: "Newsela Assignments", title: "Newsela | Assignments", favicon: "/cloaks/newsela.png" },
	"newsela-home": { label: "Newsela Home", title: "Newsela | Instructional Content Platform", favicon: "/cloaks/newsela.png" },
	"powerschool-sign-in": { label: "PowerSchool Sign In", title: "Student and Parent Sign In", favicon: "/cloaks/powerschool.png" },
	"powerschool-grades-and-attendance": { label: "PowerSchool Grades and Attendance", title: "Grades and Attendance", favicon: "/cloaks/powerschool.png" },
	"powerschool-teacher-comments": { label: "PowerSchool Teacher Comments", title: "Teacher Comments", favicon: "/cloaks/powerschool.png" },
	"powerschool-standards-grades": { label: "PowerSchool Standards Grades", title: "Standards Grades", favicon: "/cloaks/powerschool.png" },
	"powerschool-attendance": { label: "PowerSchool Attendance", title: "Attendance", favicon: "/cloaks/powerschool.png" },
	nearpod: { label: "Nearpod", title: "Nearpod", favicon: "/cloaks/nearpod.png" },
	studentvue: { label: "StudentVUE", title: "StudentVUE", favicon: "/cloaks/studentvue.ico" },
	"quizlet-home": { label: "Quizlet Home", title: "Flashcards, learning tools and textbook solutions | Quizlet", favicon: "/cloaks/quizlet.webp" },
	"google-forms-locked-mode": { label: "Google Forms Locked Mode", title: "Start your quiz", favicon: "/cloaks/googleforms.png" },
	deltamath: { label: "DeltaMath", title: "DeltaMath", favicon: "/cloaks/deltamath.png" },
	kami: { label: "Kami", title: "Kami", favicon: "/cloaks/kami.png" },
	"goguardian-admin-restricted": { label: "GoGuardian Admin Restricted", title: "Restricted", favicon: "/cloaks/goguardian-lock.png" },
	"goguardian-teacher-block": { label: "GoGuardian Teacher Block", title: "Uh oh!", favicon: "/cloaks/goguardian.png" },
	"world-history-encyclopedia": { label: "World History Encyclopedia", title: "World History Encyclopedia", favicon: "/cloaks/worldhistoryencyclopedia.png" },
	"big-ideas-math-assignment-player": { label: "Big Ideas Math Assignment Player", title: "Assignment Player", favicon: "/cloaks/bim.ico" },
	"big-ideas-math": { label: "Big Ideas Math", title: "Big Ideas Math", favicon: "/cloaks/bim.ico" },
	custom: { label: "Custom…", title: "", favicon: "" },
};

export const SEARCH_TEMPLATES = {
	duckduckgo: "https://duckduckgo.com/?q=%s",
	google: "https://www.google.com/search?q=%s",
	bing: "https://www.bing.com/search?q=%s",
};

// --- Page engines + connections: ONE label map --------------------------------
// Settings, the command palette, the connection dot's tooltip and the error overlay all name
// engines and transports from these lists, so what a student reads in one place matches the
// others exactly.
export const ENGINE_CHOICES = [["scramjet2", "Standard"], ["scramjet", "Standard (v1)"], ["uv", "Compatible"]];
export const ENGINE_LABELS = /* @__PURE__ */ Object.fromEntries(ENGINE_CHOICES);
export const TRANSPORT_CHOICES = [["libcurl", "Stable"], ["epoxy", "Fast"], ["server", "Server"]];
export const TRANSPORT_LABELS = /* @__PURE__ */ Object.fromEntries(TRANSPORT_CHOICES);
export const normalizeEngine = (v) => (Object.prototype.hasOwnProperty.call(ENGINE_LABELS, v) ? v : "scramjet2");
export const normalizeTransport = (v) => (Object.prototype.hasOwnProperty.call(TRANSPORT_LABELS, v) ? v : "libcurl");
export const engineLabel = (v) => ENGINE_LABELS[normalizeEngine(v)];
export const transportLabel = (v) => TRANSPORT_LABELS[normalizeTransport(v)];

// Why the engine or connection in use differs from the saved setting (the "(auto, ...)" cases).
export const AUTO_REASONS = {
	site: "Switched automatically for this site.",
	paused: "Standard is paused for now, so pages open with Standard (v1).",
	browser: "This browser needs Standard (v1).",
	worker: "Standard starts once all Re:Vault tabs are closed and reopened.",
	start: "Standard did not start, so pages open with Standard (v1).",
	connection: "Connection switched automatically for this site.",
	manual: "Chosen for this page from the tab menu.",
};

// One line for the connection dot: "Standard · Stable" when everything matches Settings, and
// "<in use>, switched automatically. Your setting: <saved>" for whichever part was switched, plus a
// second line saying why.
export function connectionSummary(s) {
	const eng = normalizeEngine(s.engine), savedEng = normalizeEngine(s.savedEngine);
	const tr = normalizeTransport(s.transport), savedTr = normalizeTransport(s.savedTransport);
	const engMoved = eng !== savedEng, trMoved = tr !== savedTr;
	let line = ENGINE_LABELS[eng];
	if (engMoved) line += s.engineWhy === "manual" ? " for this page" : ", switched automatically";
	line += " \u00b7 " + TRANSPORT_LABELS[tr];
	if (trMoved) line += ", switched automatically";
	const yours = [];
	if (engMoved) yours.push(ENGINE_LABELS[savedEng]);
	if (trMoved) yours.push(TRANSPORT_LABELS[savedTr]);
	if (yours.length) line += ". Your setting: " + yours.join(" \u00b7 ");
	const why = [];
	if (engMoved && AUTO_REASONS[s.engineWhy]) why.push(AUTO_REASONS[s.engineWhy]);
	if (trMoved) why.push(AUTO_REASONS.connection);
	return why.length ? line + "\n" + why.join(" ") : line;
}

// URL settings are re-checked where they are read, not only when typed in Settings: a value can
// also arrive from an imported backup or from any page on this origin writing the stored settings.
// panicUrl must be https (app/cloak.js). linuxWispUrl (linux/main.js) must be this origin's own
// ws(s) relay, or the exact other address the user confirmed in Settings. That confirmation lives
// under LINUX_RELAY_OK_KEY, outside revault:*, so no savefile or cloud backup carries it.
export const LINUX_RELAY_OK_KEY = "rv:linux-relay-ok";
export function safeLinuxWispUrl(value, confirmed, host) {
	const w = typeof value === "string" ? value.trim() : "";
	if (!w) return "";
	try { const u = new URL(w); return /^wss?:$/.test(u.protocol) && (u.host === host || w === confirmed) ? w : ""; } catch { return ""; }
}

// The stored relay when it is set but not usable (another host, never confirmed on this device):
// shown on the Linux page and in Settings so it is never silently ignored. Returns its host or "".
export function unconfirmedRelayHost(value, confirmed, host) {
	const w = typeof value === "string" ? value.trim() : "";
	if (!w || safeLinuxWispUrl(w, confirmed, host)) return "";
	try { const u = new URL(w); return /^wss?:$/.test(u.protocol) ? u.host : ""; } catch { return ""; }
}

// The move to Clean Solid (version 4). The pre-paint runs this before anything is drawn (build.mjs
// bakes in this exact function, so it has to stand alone: no imports, no constants from this file),
// and migrateSettings runs it on any stored blob the pre-paint hasn't settled yet. s is the saved
// blob, ls is localStorage. Returns true when s changed and should be saved.
//
// Saved settings hold defaults too, so a stored "bedazzled" may never have been a choice: everyone
// on it moves once, and the accent, font and corners follow only if they are still Bedazzled's. Any
// other look stays exactly as it is. From version 4 on, "bedazzled" is a choice and is left alone.
// A blob with no theme means Bedazzled too, unless rv:look says "new": the focus timer writes its
// settings straight into the blob, so a new visitor can have one without ever seeing Bedazzled.
//
//   rv:look       "kept": undid the move, so never move again. "new": first seen after this shipped.
//   rv:look-was   what the first move replaced, for the undo. Later moves leave it alone.
//   rv:look-toast the undo offer is waiting to be shown.
//
// An open tab from before this release can still write the blob back with _v:2, which is why a
// repeat is quiet and why "kept" beats the version. Below version 2 the version stays put so
// migrateSettings still does its own step afterwards. rv:* never goes into backups.
export function settleLook(s, ls) {
	if (s._v >= 4) return false;
	var seen = ls.getItem("rv:look"), was = { theme: "bedazzled" }, i,
		swap = { accent: ["#e9c46a", "#f6b93b"], font: ["sora", "rounded"], cornerStyle: ["soft", "rounded"] };
	if (!Object.keys(s).length) {
		// No saved settings: a first visit, or someone who never changed one and has seen Bedazzled.
		if (seen) return false;
		for (i = ls.length; i-- && ls.key(i).indexOf("revault:"););
		if (i < 0) { ls.setItem("rv:look", "new"); return false; }
	}
	if ((s.theme === "bedazzled" || (!s.theme && seen !== "new")) && seen !== "kept") {
		s.theme = "clean";
		for (i in swap) {
			was[i] = s[i] || swap[i][0];
			if (was[i] === swap[i][0]) s[i] = swap[i][1];
		}
		if (!ls.getItem("rv:look-was")) {
			ls.setItem("rv:look-was", JSON.stringify(was));
			ls.setItem("rv:look-toast", "1");
		}
	}
	if (s._v >= 2) s._v = 4;
	return true;
}

// Saved-settings migration. persist() always wrote the WHOLE settings object, defaults included,
// so a stored engine:"scramjet" from before version 2 cannot be told apart from "never touched":
// it is read as the old default and moves to the new default (Scramjet 2) once. From version 2
// on, "scramjet" is an explicit choice of Standard (v1) and is kept. Also drops the dead
// scramjetCodec key. Then the look (settleLook above). Mutates obj; returns true when anything changed.
export function migrateSettings(obj, ls = typeof localStorage === "undefined" ? null : localStorage) {
	if (!obj || typeof obj !== "object") return false;
	let changed = false;
	const v = Number(obj._v) || 0;
	if (v < 2) {
		if (obj.engine === "scramjet") { obj.engine = "scramjet2"; changed = true; }
		if (Object.prototype.hasOwnProperty.call(obj, "scramjetCodec")) { delete obj.scramjetCodec; changed = true; }
		obj._v = 2;
		changed = true;
	}
	if (v < SETTINGS_VERSION) {
		// With storage full or off the look stays put; the next pre-paint tries again.
		try { if (ls && settleLook(obj, ls)) changed = true; } catch { /* see above */ }
	}
	return changed;
}

// The BareMux option key each transport expects (module paths come from the
// build-injected manifest in proxy.js so they stay cloaked).
export const TRANSPORTS = {
	libcurl: { key: "websocket" },
	epoxy: { key: "wisp" },
};

// Built-in background-music soundscapes. Each is generated live with the Web Audio
// API (app/music.js) - zero files, no external hosts, works under the app's
// cross-origin isolation. "uploads" (handled specially) plays your own files.
export const MUSIC_PRESETS = [
	{ id: "brown", label: "Brown Noise" },
	{ id: "white", label: "White Noise" },
	{ id: "rain", label: "Rain" },
	{ id: "ocean", label: "Ocean Waves" },
	{ id: "fire", label: "Fireplace" },
	{ id: "focus", label: "Focus Hum" },
];
export const MUSIC_PRESET_MAP = /* @__PURE__ */ Object.fromEntries(/* @__PURE__ */ MUSIC_PRESETS.map((m) => [m.id, m]));

// localStorage key for the uploaded-music index (track metadata only; the audio
// blobs live in IndexedDB - see shared/music-store.js).
export const MUSIC_KEY = "revault:music";
